import { can, type OrganisationRole } from '@evw/domain';
import { Button, Card } from '@evw/ui';
import { decimalToMinorUnits } from '@evw/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { archiveCoreRecord, createService, listServices, updateService } from '../tenancy/api';

export function ServicesPage({
  organisationId,
  role,
  currency
}: {
  organisationId: string;
  role: OrganisationRole;
  currency: string;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState('0.00');
  const [editingId, setEditingId] = useState<string | null>(null);
  const fractionDigits =
    new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency
    }).resolvedOptions().maximumFractionDigits ?? 2;
  const decimalStep = fractionDigits === 0 ? '1' : `0.${'0'.repeat(fractionDigits - 1)}1`;
  const query = useQuery({
    queryKey: ['services', organisationId],
    queryFn: () => listServices(organisationId)
  });
  const save = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        duration_minutes: duration,
        gross_amount_minor: decimalToMinorUnits(price, fractionDigits),
        currency
      };
      return editingId
        ? updateService(organisationId, editingId, input)
        : createService(organisationId, input);
    },
    onSuccess: async () => {
      setName('');
      setDuration(60);
      setPrice('0.00');
      setEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ['services', organisationId] });
    }
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveCoreRecord('services', organisationId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['services', organisationId] })
  });
  const editable = can(role, 'create', 'service');
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }
  const money = new Intl.NumberFormat('en-NZ', { style: 'currency', currency });
  return (
    <div className="stack">
      <Card>
        <h2>Services</h2>
        <p className="muted">Reusable services, durations and current prices.</p>
        {editable ? (
          <form className="inline-form" onSubmit={submit}>
            <label>
              Name
              <input
                required
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label>
              Duration (minutes)
              <input
                type="number"
                min="5"
                max="1440"
                required
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              />
            </label>
            <label>
              Price ({currency})
              <input
                type="number"
                min="0"
                step={decimalStep}
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </label>
            <div className="form-actions">
              <Button disabled={save.isPending}>
                {editingId ? 'Save service' : 'Add service'}
              </Button>
              {editingId ? (
                <Button
                  className="button-secondary"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setName('');
                    setDuration(60);
                    setPrice('0.00');
                  }}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </form>
        ) : null}
        {save.error ? <p className="error">{save.error.message}</p> : null}
      </Card>
      <Card>
        <div className="records">
          {query.isLoading ? (
            <p>Loading…</p>
          ) : query.error ? (
            <p className="error">{query.error.message}</p>
          ) : query.data?.length ? (
            query.data.map((service) => (
              <article className="record" key={service.id}>
                <div>
                  <strong>{service.name}</strong>
                  <p>
                    {service.duration_minutes} min ·{' '}
                    {money.format(service.gross_amount_minor / 10 ** fractionDigits)}
                  </p>
                </div>
                {editable ? (
                  <div className="record-actions">
                    <Button
                      className="button-secondary"
                      onClick={() => {
                        setEditingId(service.id);
                        setName(service.name);
                        setDuration(service.duration_minutes);
                        setPrice(minorUnitsToDecimal(service.gross_amount_minor, fractionDigits));
                      }}
                    >
                      Edit
                    </Button>
                    <Button className="button-secondary" onClick={() => archive.mutate(service.id)}>
                      Archive
                    </Button>
                  </div>
                ) : null}
              </article>
            ))
          ) : (
            <p className="muted">No services yet.</p>
          )}
        </div>
      </Card>
    </div>
  );
}

function minorUnitsToDecimal(value: number, fractionDigits: number) {
  if (fractionDigits === 0) return String(value);
  const scale = 10 ** fractionDigits;
  const whole = Math.trunc(value / scale);
  const fraction = String(value % scale).padStart(fractionDigits, '0');
  return `${whole}.${fraction}`;
}
